import {DateTime} from 'luxon';
// https://moment.github.io/luxon/#/formatting?id=table-of-tokens

/**
 * @description   2021-03-15T08:30:00 -> 3.15
 * @param date
 */
export function formatDate(date: string | DateTime): string {
    let dateTime;
    if (typeof date == 'string') {
        dateTime = DateTime.fromISO(date);
    } else {
        dateTime = date;
    }
    return dateTime.toFormat('L.d');
}

/**
 * @description  2021-03-15T08:30:00 -> 08:30
 * @param date
 */
export function formatTime(date?: string): string {
    if (!date) return '';
    const dateTime = DateTime.fromISO(date);
    return dateTime.toFormat('T');
}

/**
 * @description   2021-03-15T08:30:00 -> 화
 * @param date
 */
export function formatKoreanWeekday(date: string): string {
    const dateTime = DateTime.fromISO(date);
    const dayNumber = dateTime.toFormat('c');

    switch (Number(dayNumber)) {
        case 1:
            return '월';
        case 2:
            return '화';
        case 3:
            return '수';
        case 4:
            return '목';
        case 5:
            return '금';
        case 6:
            return '토';
        case 7:
            return '일';
        default:
            return '';
    }
}

/**
 * @description  두 날짜의 차이 ('days')
 * @param date
 */
export function getDiffDays(first: string, second: string) {
    const firstDate = DateTime.fromISO(first).startOf('day');
    const secondDate = DateTime.fromISO(second).startOf('day');
    const diff = firstDate.diff(secondDate, ['days']);
    return diff['days'];
}

/**
 * @description 2023-04-11T18:30:00 → 2023-04-11 또는 2023-04-11 18:30
 * @param date - ISO 포맷의 실제 존재하는 날짜
 * @param options - 포맷 옵션 (날짜 표기 separator string, 시간 표기 유무)
 */
export function formatDateTime24(
    date: string,
    options?: {separator?: string; hasTime?: boolean},
) {
    if (!date) return date;

    const dateTime = DateTime.fromISO(date);

    if (!dateTime.isValid) {
        return date;
    }

    let formatExp = dateTime.toFormat('yyyy-MM-dd');

    if (!options) {
        return dateTime.toFormat(formatExp);
    }

    formatExp = options.hasTime
        ? `yyyy${options.separator ?? '-'}MM${options.separator ?? '-'}dd HH:mm`
        : `yyyy${options.separator ?? '-'}MM${options.separator ?? '-'}dd`;

    return dateTime.toFormat(formatExp);
}

/**
 * @description   2021-03-15T08:30:00 -> 2021.03.15
 * @param date
 */
export function formatYearDate(date: string): string {
    const dateTime = DateTime.fromISO(date);
    return dateTime.toFormat('yyyy.LL.dd');
}

/**
 * @description   2021-03-15T08:30:00 -> 21.03.15
 * @param date
 */
export function formatShortYearDate(date: string): string {
    const dateTime = DateTime.fromISO(date);
    return dateTime.toFormat('yy.LL.dd');
}

/**
 * @description  2021-03-15T08:30:00 -> 2021.03.15 (수)
 * @param date
 */
export function formatDateTimeWeekday(date?: string) {
    if (!date) return '';
    const time = DateTime.fromISO(date);
    const dateTimeStr = time.toFormat(
        `yyyy.LL.dd (${formatKoreanWeekday(date)})`,
    );
    return dateTimeStr;
}

/**
 * @description  2021-03-15T08:30:00 -> 21.03.15 (수)
 * @param date
 */
export function formatShortDateTimeWeekday(date?: string) {
    if (!date) return '';
    const dateTime = DateTime.fromISO(date);
    if (dateTime.isValid) {
        const dateTimeStr = dateTime.toFormat(
            `yy.LL.dd (${formatKoreanWeekday(date)})`,
        );
        return dateTimeStr;
    }
    return '';
}

/**
 * @description   8월 14일 (토) 오전 10:00 (렌트카)
 * @param date
 */
export function formatOrderDateTime(date?: string) {
    if (!date) return '';
    const time = DateTime.fromISO(date);
    let dateTimeStr = time.toFormat(
        `M월 d일 (${formatKoreanWeekday(date)}) a hh:mm`,
    );
    dateTimeStr = dateTimeStr.replace('AM', '오전');
    dateTimeStr = dateTimeStr.replace('PM', '오후');
    return dateTimeStr;
}

/**
 * @description  // 8월 14일 (토) 22:00 (공간대여)
 * @param date
 */
export function formatOrderDate24Time(date: string) {
    if (!date) return '';
    const time = DateTime.fromISO(date);
    return time.toFormat(`M월 d일 (${formatKoreanWeekday(date)}) HH:mm`);
}

/**
 * @description  8월 14일 22:00
 * @param date
 */
export function formatMDHHmm(date: string | DateTime) {
    let dateTime;
    if (typeof date == 'string') {
        dateTime = DateTime.fromISO(date);
    } else {
        dateTime = date;
    }
    if (!dateTime.isValid) {
        throw new Error('formatMDHHmm: date값이 유효하지 않습니다');
    }
    return dateTime.toFormat(`M월 d일 HH:mm`);
}

/**
 * @description 23년 9월 6일 22:00
 * @param date
 */
export function formatYYMDHHmm(date: string | Date) {
    if (!date) return '';

    let dateTime = '' as unknown as DateTime;

    if (date instanceof Date) {
        dateTime = DateTime.fromJSDate(date);
    }

    if (typeof date === 'string') {
        const ISO_DATE_REGEX =
            /^\d{4}-(?:0[1-9]|1[0-2])-(?:[0-2][1-9]|[1-3]0|3[01])T(?:[0-1][0-9]|2[0-3])(?::[0-6]\d)(?::[0-6]\d)?(?:\.\d{3})?(?:[+-][0-2]\d:[0-5]\d|Z)?$/;
        const isIsoDate = ISO_DATE_REGEX.test(date);

        dateTime = isIsoDate
            ? DateTime.fromISO(date)
            : DateTime.fromJSDate(new Date(date));
    }

    if (!dateTime.isValid) {
        throw new Error(
            'formatYYMDHHmm: 존재하지 않는 날짜 또는 시간이거나, date가 ISO 형식 또는 JavaScript Date 형식이 아닙니다.',
        );
    }

    return dateTime.toFormat(`yy년 M월 d일 HH:mm`);
}

/**
 * @description 23.9.6 22:00
 * @param date
 */
export function formatYYMDHHmmWithDots(date: string | Date) {
    if (!date) return '';

    let dateTime = '' as unknown as DateTime;

    if (date instanceof Date) {
        dateTime = DateTime.fromJSDate(date);
    }

    if (typeof date === 'string') {
        const ISO_DATE_REGEX =
            /^\d{4}-(?:0[1-9]|1[0-2])-(?:[0-2][1-9]|[1-3]0|3[01])T(?:[0-1][0-9]|2[0-3])(?::[0-6]\d)(?::[0-6]\d)?(?:\.\d{3})?(?:[+-][0-2]\d:[0-5]\d|Z)?$/;
        const isIsoDate = ISO_DATE_REGEX.test(date);

        dateTime = isIsoDate
            ? DateTime.fromISO(date)
            : DateTime.fromJSDate(new Date(date));
    }

    if (!dateTime.isValid) {
        throw new Error(
            'formatYYMDHHmmWithDots: 존재하지 않는 날짜 또는 시간이거나, date가 ISO 형식 또는 JavaScript Date 형식이 아닙니다.',
        );
    }

    return dateTime.toFormat(`yy.M.d HH:mm`);
}
